import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { NavigateFunction } from "react-router-dom";
import { OrangeTextButton } from "../OrangeTextButton";
import { ActuateButton } from "../ActuateButton";
import { useImportSettingsStore } from "./importSettingsStore";
import { ImportPolygon } from "../../utils/importSettingsApi";
import { markSiteUnsynced } from "../../utils/syncStore";

export const IMPORT_OVERLAY_COLOR = "#7C3AED";

export const noExclusionAreasMessage = (integrationLabel: string) =>
  `No exclusion areas on ${integrationLabel} for this camera.`;

/**
 * "Import from <Integration>" for one camera's ignore-zone editor (ENG-2461).
 *
 * Click → preview for this camera → the polygons go to `onPreview` so the
 * page can draw them as a dashed overlay → Apply / Cancel. Apply asks
 * "Replace the current zones?" when the camera already has zones (then sends
 * `overwrite: true`), and calls `onApplied` so the page reloads the zones
 * the normal way. Privacy masks ride along with the zones when offered.
 */
export const ImportZonesFromVmsButton = ({
  siteId,
  cameraId,
  integrationLabel,
  offeredSettings,
  hasExistingZones,
  navigate,
  onPreview,
  onApplied,
}: {
  siteId: string | number;
  cameraId: number;
  integrationLabel: string;
  offeredSettings: string[];
  /** The editor already shows zones for this camera. */
  hasExistingZones: boolean;
  navigate: NavigateFunction;
  /** Polygons to draw while previewing; `null` clears the overlay. */
  onPreview: (polygons: ImportPolygon[] | null) => void;
  onApplied: () => void;
}) => {
  const [previewing, setPreviewing] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const preview = useImportSettingsStore((s) => s.preview);
  const previewLoading = useImportSettingsStore((s) => s.previewLoading);
  const previewError = useImportSettingsStore((s) => s.previewError);
  const applying = useImportSettingsStore((s) => s.applying);
  const loadPreview = useImportSettingsStore((s) => s.loadPreview);
  const apply = useImportSettingsStore((s) => s.apply);
  const reset = useImportSettingsStore((s) => s.reset);

  const settings = React.useMemo(() => {
    const out: string[] = [];
    if (offeredSettings.includes("zones")) out.push("zones");
    if (offeredSettings.includes("masks")) out.push("masks");
    return out;
  }, [offeredSettings]);

  const cameraPreview = React.useMemo(
    () => preview?.cameras.find((camera) => camera.id === cameraId) ?? null,
    [preview, cameraId]
  );

  const polygons = React.useMemo<ImportPolygon[]>(() => {
    if (!cameraPreview) return [];
    return [
      ...(cameraPreview.zones?.polygons ?? []),
      ...(cameraPreview.masks?.polygons ?? []),
    ];
  }, [cameraPreview]);

  const sourceHasExisting = Boolean(
    cameraPreview?.zones?.has_existing || cameraPreview?.masks?.has_existing
  );

  // Hand the overlay to the page whenever the preview changes while active.
  React.useEffect(() => {
    if (!previewing) return;
    if (previewError) {
      setNotice(previewError);
      setPreviewing(false);
      onPreview(null);
      return;
    }
    if (!preview) return;
    if (polygons.length === 0) {
      setNotice(noExclusionAreasMessage(integrationLabel));
      setPreviewing(false);
      onPreview(null);
      return;
    }
    onPreview(polygons);
    // onPreview is a page setter; re-running on its identity is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewing, preview, previewError, polygons, integrationLabel]);

  React.useEffect(
    () => () => {
      onPreview(null);
      reset();
    },
    // Unmount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const start = () => {
    setNotice(null);
    reset();
    setPreviewing(true);
    void loadPreview(siteId, [cameraId], settings, navigate);
  };

  const cancel = () => {
    setPreviewing(false);
    setConfirmOpen(false);
    onPreview(null);
    reset();
  };

  const runApply = (overwrite: boolean) => {
    setConfirmOpen(false);
    void apply(siteId, [cameraId], settings, overwrite, navigate).then(
      (response) => {
        if (!response) {
          setNotice(useImportSettingsStore.getState().applyError);
          return;
        }
        markSiteUnsynced(String(siteId));
        setPreviewing(false);
        onPreview(null);
        reset();
        onApplied();
      }
    );
  };

  const handleApply = () => {
    if (hasExistingZones || sourceHasExisting) {
      setConfirmOpen(true);
      return;
    }
    runApply(false);
  };

  const overlayShown = previewing && polygons.length > 0 && !previewLoading;

  return (
    <div className="flex items-center gap-2">
      {notice && (
        <span
          className="font-mulish text-[13px] text-gray-600"
          data-testid="import-zones-notice"
        >
          {notice}
        </span>
      )}
      {previewing && previewLoading && (
        <CircularProgress color="warning" size={18} />
      )}
      {!overlayShown && (
        <OrangeTextButton
          onClick={start}
          text={`Import from ${integrationLabel}`}
          disabled={previewing && previewLoading}
        />
      )}
      {overlayShown && (
        <>
          <OrangeTextButton
            onClick={cancel}
            text="Cancel"
            disabled={applying}
          />
          <ActuateButton
            onClick={handleApply}
            text={applying ? "Applying…" : "Apply"}
            disabled={applying}
          />
        </>
      )}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="import-zones-replace-title"
      >
        <DialogTitle
          id="import-zones-replace-title"
          sx={{ fontFamily: "Mulish", color: "#001943", fontWeight: "bold" }}
        >
          Replace the current zones?
        </DialogTitle>
        <DialogContent>
          <p className="font-mulish text-actuate-blue text-[14px]">
            This camera already has ignore zones. Importing from{" "}
            {integrationLabel} replaces them with the exclusion areas shown.
          </p>
        </DialogContent>
        <DialogActions>
          <OrangeTextButton
            onClick={() => setConfirmOpen(false)}
            text="Keep current zones"
            disabled={false}
          />
          <ActuateButton
            onClick={() => runApply(true)}
            text="Replace"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

/**
 * Dashed preview of the polygons the import would create, drawn over the
 * frame at the editor's adjusted size. Coordinates are frame fractions.
 */
export const ImportedZonesOverlay = ({
  polygons,
  width,
  height,
}: {
  polygons: ImportPolygon[];
  width: number;
  height: number;
}) => {
  if (polygons.length === 0) return null;
  return (
    <svg
      className="absolute top-[0px] left-[0px] pointer-events-none"
      style={{ width: `${width}px`, height: `${height}px` }}
      data-testid="import-zones-overlay"
    >
      {polygons.map((polygon, index) => (
        <polygon
          key={index}
          points={polygon
            .map(([x, y]) => `${x * width},${y * height}`)
            .join(" ")}
          stroke={IMPORT_OVERLAY_COLOR}
          strokeWidth="3"
          strokeDasharray="8 6"
          fill={IMPORT_OVERLAY_COLOR}
          fillOpacity="0.15"
        />
      ))}
    </svg>
  );
};
